import type { Site } from '@hotjar/site';
import type { Account, OwnedAccount, SiteAccount } from '@hotjar/account';
import type { OrganizationAccess } from '@hotjar/organization';
import { track as segmentTrack } from './tools/segment';
import { tagRecording } from './tag-recording';

export type TrackingSite = Pick<Site, 'id'> & { account: Pick<Site['account'], 'id'> } & {
  organization: Pick<Site['organization'], 'id'>;
};

export type SiteAccess = {
  account: Pick<Site['account'], 'access'>;
} & {
  organization: Pick<Site['organization'], 'access'>;
};

export type PermissionLevelLabel = 'Admin' | 'Account owner' | 'Read-only' | 'Read and write';
type PermissionLevel = OrganizationAccess | 'owner';
const permissionLevelNames: Readonly<Record<PermissionLevel, PermissionLevelLabel>> = Object.freeze(
  {
    admin: 'Admin',
    owner: 'Account owner',
    read: 'Read-only',
    write: 'Read and write',
  },
);

export interface SiteGroupingData {
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  site_id: number;
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  organization_id: number;
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  account_id: number;
}

export interface OrganizationGroupingData {
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  organization_id: number;
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  account_id: number;
}

interface GroupingOrganization {
  id: number;
  account: {
    id: number;
  };
}

export interface AccountGroupingData {
  // FIXME: camelcase
  // eslint-disable-next-line camelcase
  account_id: number;
}

/**
 * Output a console.error when a tracking failure occurs.
 * NOTE: This still allows the code to keep executing since we don't
 * Throw an error, but rather simply log it to console.
 *
 * @param message {string} Message to output.
 */
export const logTrackingError = (message: string): void => {
  /* eslint-disable no-console */
  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    console.error(`TRACKING FAILURE: ${message}`);
  }
  /* eslint-enable no-console */
};

/**
 * Get the user permission level.
 *
 * @param site Site to get group data for.
 * @return {string} the permission level.
 */
export const extractPermissionLevel = (
  site: SiteAccess | undefined,
): PermissionLevelLabel | undefined => {
  const permissionLevel: PermissionLevel | undefined =
    site?.account?.access === 'admin' ? 'owner' : site?.organization?.access;
  if (!permissionLevel) {
    return undefined;
  }
  return permissionLevelNames[permissionLevel];
};

/**
 * Get tracking group data for a site.
 *
 * @param site Site to get group data for.
 * @return {object} Object referencing the site id, organization id and account id.
 */
export const extractSiteGroupingProps = (
  site: TrackingSite | undefined,
): SiteGroupingData | undefined => {
  if (
    typeof site?.id === 'undefined' ||
    typeof !site?.organization?.id === 'undefined' ||
    typeof !site?.account?.id === 'undefined'
  ) {
    logTrackingError('Unable to group data by site, organization and account.');
    return undefined;
  }
  return {
    site_id: site.id,
    organization_id: site.organization.id,
    account_id: site.account.id,
  };
};

/**
 * Get tracking group data for an organization.
 */
export const extractOrganizationGroupingProps = (
  organization: GroupingOrganization | undefined,
): OrganizationGroupingData | undefined => {
  if (!organization || !organization.id || !organization.account?.id) {
    logTrackingError('Unable to group data by organization and account.');
    return undefined;
  }

  return {
    organization_id: organization.id,
    account_id: organization.account.id,
  };
};

/**
 * Get tracking group data for an organization.
 */
export const extractAccountGroupingProps = (
  account: Account | OwnedAccount | SiteAccount | undefined,
): AccountGroupingData | undefined => {
  if (!account || typeof account.id === 'undefined') {
    logTrackingError('Unable to group data by account.');
    return undefined;
  }

  return {
    account_id: account.id,
  };
};

/**
 * Track when user leaves or navigates somewhere else while a page, tool, or feature is still loading.
 *
 * @param currentSite The current site the user is navigating
 * @param fromEvent The event that would have fired
 * @param waitTimeMilliseconds Time before user left (in milliseconds)
 */
export const trackBounce = (
  currentSite: Site,
  fromEvent: string,
  waitTimeMilliseconds: number,
): void => {
  const eventName = 'User Bounced';
  const data = extractSiteGroupingProps(currentSite) || {};

  segmentTrack(eventName, {
    ...data,
    from_event: fromEvent,
    wait_time_milliseconds: waitTimeMilliseconds,
  });
  tagRecording({ name: `${eventName} - ${fromEvent}` });
};
